import React from 'react';
import { Button, ListItemIcon, ListItemText, Box } from '@mui/material';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  BorderStyle,
} from 'docx';
import { saveAs } from 'file-saver';
import { Download, FileText, FileEdit, File, FileType } from 'lucide-mui';
import { jsPDF } from 'jspdf';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import ReactDOMServer from 'react-dom/server';
import useLimits from '../../plan/hooks/limits';
import ActionDialog from '../../globalModals/ActionDialog';

interface DownloadFileProps {
  text: string;
  fileName: string;
  id?: string;
  variant?: 'icon' | 'button';
  onClick?: () => void;
}

const stripCitations = (text: string): string => {
  // Matches [→](number) pattern
  return text.replace(/\s*\[→\]\(\d+\)/g, '');
};

const DownloadFile = ({
  text,
  fileName,
  id,
  variant = 'button',
  onClick,
}: DownloadFileProps) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const guardLimits = useLimits();

  const handleUpgrade = () => {
    guardLimits('downloadTranscript', () => {
      // Handle upgrade logic here if needed
    });
  };

  const handleDownload = (downloadFunction: () => void) => {
    if (guardLimits('downloadTranscript')) {
      downloadFunction();
      setDialogOpen(false);
    } else {
      handleUpgrade();
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const downloadTxt = () => {
    const cleanText = stripCitations(text);
    const element = document.createElement('a');
    const file = new Blob([cleanText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${fileName}-transcript.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    handleClose();
  };

  const renderMarkdown = (markdown: string): HTMLDivElement => {
    const cleanMarkdown = stripCitations(markdown);
    const tempDiv = document.createElement('div');
    const root = document.createElement('div');

    const htmlContent = ReactDOMServer.renderToString(
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanMarkdown}</ReactMarkdown>
    );

    root.innerHTML = DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: [
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'p',
        'a',
        'strong',
        'em',
        'b',
        'i',
        'ul',
        'ol',
        'li',
        'blockquote',
        'code',
        'pre',
        'hr',
        'br',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
      ],
    });

    tempDiv.appendChild(root);
    return tempDiv;
  };

  const parseMarkdownToDocxElements = (markdown: string) => {
    const tempDiv = renderMarkdown(markdown);
    const elements: (Paragraph | Table)[] = [];

    const processNode = (node: HTMLElement): (Paragraph | Table)[] => {
      const result: (Paragraph | Table)[] = [];

      switch (node.tagName.toLowerCase()) {
        case 'h1': {
          const fontSize = 32;
          const spacing = { before: 240, after: 120 };
          result.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: node.textContent || '',
                  font: 'Arial',
                  color: '000000',
                  bold: true,
                  size: fontSize,
                }),
              ],
              heading: HeadingLevel.HEADING_1,
              spacing,
            })
          );
          break;
        }
        case 'h2': {
          const fontSize = 28;
          const spacing = { before: 240, after: 120 };
          result.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: node.textContent || '',
                  font: 'Arial',
                  color: '000000',
                  bold: true,
                  size: fontSize,
                }),
              ],
              heading: HeadingLevel.HEADING_2,
              spacing,
            })
          );
          break;
        }
        case 'h3': {
          const fontSize = 24;
          const spacing = { before: 240, after: 120 };
          result.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: node.textContent || '',
                  font: 'Arial',
                  color: '000000',
                  bold: true,
                  size: fontSize,
                }),
              ],
              heading: HeadingLevel.HEADING_3,
              spacing,
            })
          );
          break;
        }
        case 'h4': {
          const fontSize = 22;
          const spacing = { before: 240, after: 120 };
          result.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: node.textContent || '',
                  font: 'Arial',
                  color: '000000',
                  bold: true,
                  size: fontSize,
                }),
              ],
              heading: HeadingLevel.HEADING_4,
              spacing,
            })
          );
          break;
        }
        case 'h5': {
          const fontSize = 20;
          const spacing = { before: 240, after: 120 };
          result.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: node.textContent || '',
                  font: 'Arial',
                  color: '000000',
                  bold: true,
                  size: fontSize,
                }),
              ],
              heading: HeadingLevel.HEADING_5,
              spacing,
            })
          );
          break;
        }
        case 'h6': {
          const fontSize = 18;
          const spacing = { before: 240, after: 120 };
          result.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: node.textContent || '',
                  font: 'Arial',
                  color: '000000',
                  bold: true,
                  size: fontSize,
                }),
              ],
              heading: HeadingLevel.HEADING_6,
              spacing,
            })
          );
          break;
        }
        case 'blockquote':
          result.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: node.textContent || '',
                  font: 'Arial',
                  color: '000000',
                  italics: true,
                }),
              ],
              indent: { left: 720 },
              spacing: { before: 120, after: 120 },
            })
          );
          break;
        case 'code':
        case 'pre':
          result.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: node.textContent || '',
                  font: 'Courier New',
                  color: '000000',
                }),
              ],
              spacing: { before: 120, after: 120 },
            })
          );
          break;
        case 'p':
          result.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: node.textContent || '',
                  font: 'Arial',
                  color: '000000',
                }),
              ],
              spacing: { before: 120, after: 120 },
            })
          );
          break;
        case 'ul':
        case 'ol':
          Array.from(node.children).forEach((li, index) => {
            result.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text:
                      node.tagName.toLowerCase() === 'ol'
                        ? `${index + 1}. `
                        : '• ',
                    font: 'Arial',
                    color: '000000',
                  }),
                  new TextRun({
                    text: li.textContent || '',
                    font: 'Arial',
                    color: '000000',
                  }),
                ],
                indent: { left: 720 },
                spacing: { before: 60, after: 60 },
              })
            );
          });
          break;
        case 'hr':
          result.push(
            new Paragraph({
              text: '―'.repeat(40),
              alignment: 'center',
              spacing: { before: 240, after: 240 },
            })
          );
          break;
        case 'table': {
          const rows: TableRow[] = [];
          const tableElement = node as HTMLTableElement;
          const columnCount = Math.max(
            ...Array.from(tableElement.querySelectorAll('tr')).map(
              (row) => row.cells.length
            )
          );
          Array.from(tableElement.querySelectorAll('tr')).forEach((tr) => {
            const cells = Array.from(tr.querySelectorAll('th, td')).map(
              (cell) =>
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: cell.textContent || '',
                          bold: cell.tagName.toLowerCase() === 'th',
                          font: 'Arial',
                          color: '000000',
                        }),
                      ],
                    }),
                  ],
                  borders: {
                    top: { style: BorderStyle.SINGLE, size: 1 },
                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                    left: { style: BorderStyle.SINGLE, size: 1 },
                    right: { style: BorderStyle.SINGLE, size: 1 },
                  },
                  width: {
                    size: 8500 / columnCount,
                    type: 'dxa',
                  },
                })
            );
            rows.push(new TableRow({ children: cells }));
          });

          result.push(
            new Table({
              rows,
              width: {
                size: 8500,
                type: 'dxa',
              },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1 },
                bottom: { style: BorderStyle.SINGLE, size: 1 },
                left: { style: BorderStyle.SINGLE, size: 1 },
                right: { style: BorderStyle.SINGLE, size: 1 },
              },
              alignment: 'center',
              columnWidths: Array(columnCount).fill(8500 / columnCount),
            })
          );
          break;
        }
        default:
          if (node.textContent?.trim()) {
            result.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: node.textContent,
                    font: 'Arial',
                    color: '000000',
                  }),
                ],
                spacing: { before: 120, after: 120 },
              })
            );
          }
      }
      return result;
    };

    // Process all child nodes recursively
    const processChildren = (element: HTMLElement) => {
      Array.from(element.children).forEach((child) => {
        elements.push(...processNode(child as HTMLElement));
      });
    };

    processChildren(tempDiv.firstChild as HTMLElement);
    return elements;
  };

  const convertToRtf = (markdown: string): string => {
    const tempDiv = renderMarkdown(markdown);
    let rtfContent = '{\\rtf1\\ansi\\deff0\n';
    rtfContent +=
      '{\\fonttbl{\\f0\\froman Times New Roman;}{\\f1\\fswiss Arial;}{\\f2\\fmodern Courier New;}}\n';
    rtfContent += '{\\colortbl;\\red0\\green0\\blue0;}\n';

    const processNode = (node: HTMLElement): string => {
      const content = node.textContent?.replace(/[\\{}]/g, '\\$&') || '';

      switch (node.tagName.toLowerCase()) {
        case 'h1':
          return `{\\f1\\fs48\\b ${content}}\\par\\par\n`;
        case 'h2':
          return `{\\f1\\fs36\\b ${content}}\\par\\par\n`;
        case 'h3':
          return `{\\f1\\fs32\\b ${content}}\\par\\par\n`;
        case 'h4':
          return `{\\f1\\fs28\\b ${content}}\\par\\par\n`;
        case 'h5':
          return `{\\f1\\fs24\\b ${content}}\\par\\par\n`;
        case 'h6':
          return `{\\f1\\fs20\\b ${content}}\\par\\par\n`;
        case 'blockquote':
          return `{\\f1\\fi720\\i ${content}}\\par\\par\n`;
        case 'code':
        case 'pre':
          return `{\\f2 ${content}}\\par\\par\n`;
        case 'p':
          return `{\\f1\\fs24 ${content}}\\par\\par\n`;
        case 'ul':
          return `${Array.from(node.children)
            .map(
              (li) =>
                `{\\f1\\fs24\\li720\\fi-360 \\bullet\\tab ${li.textContent}}\\par\n`
            )
            .join('')}\\par\n`;
        case 'ol':
          return `${Array.from(node.children)
            .map(
              (li, i) =>
                `{\\f1\\fs24\\li720\\fi-360 ${i + 1}.\\tab ${
                  li.textContent
                }}\\par\n`
            )
            .join('')}\\par\n`;
        case 'table': {
          let tableContent = '\\par\n';
          const tableElement = node as HTMLTableElement;
          Array.from(tableElement.querySelectorAll('tr')).forEach((tr) => {
            Array.from(tr.querySelectorAll('th, td')).forEach((cell) => {
              const isHeader = cell.tagName.toLowerCase() === 'th';
              tableContent += `{\\f1\\fs24${isHeader ? '\\b' : ''} ${
                cell.textContent
              }}\\cell `;
            });
            tableContent += '\\row\n';
          });
          return `${tableContent}\\par\n`;
        }
        default:
          if (content.trim()) {
            return `{\\f1\\fs24 ${content}}\\par\n`;
          }
          return '';
      }
    };

    const processChildren = (element: HTMLElement) => {
      Array.from(element.children).forEach((child) => {
        rtfContent += processNode(child as HTMLElement);
      });
    };

    processChildren(tempDiv.firstChild as HTMLElement);
    rtfContent += '}';
    return rtfContent;
  };

  const downloadDocx = async () => {
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: parseMarkdownToDocxElements(text),
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${fileName}-transcript.docx`);
    handleClose();
  };

  const downloadPdf = () => {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF();
    const tempDiv = renderMarkdown(text);

    let yPos = 15;
    const margin = 15;
    const pageWidth = doc.internal.pageSize.width;
    const maxWidth = pageWidth - margin * 2;
    const lineHeightMultiplier = 1.5;

    // Helper function to calculate text height with increased line height
    const calculateTextHeight = (lines: string[], fontSize: number) => {
      return lines.length * (fontSize * 0.3527777778 * lineHeightMultiplier);
    };

    // Helper function to add spacing
    const addSpacing = (size: number) => {
      yPos += size;
    };

    // Helper function to check page break
    const checkPageBreak = (height: number) => {
      if (yPos + height > doc.internal.pageSize.height - margin) {
        doc.addPage();
        yPos = margin;
      }
    };

    // Process a text block with given settings
    const addTextBlock = (
      content: string,
      fontSize: number,
      fontStyle: 'normal' | 'bold' | 'italic',
      indent = 0
    ) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', fontStyle);

      const lines = doc.splitTextToSize(content, maxWidth - indent);
      const textHeight = calculateTextHeight(lines, fontSize);

      checkPageBreak(textHeight);

      // Add each line separately with increased line height
      lines.forEach((line: string, index: number) => {
        const lineY =
          yPos + index * fontSize * 0.3527777778 * lineHeightMultiplier;
        doc.text(line, margin + indent, lineY);
      });

      yPos += textHeight;
    };

    const processNode = (node: HTMLElement) => {
      const tagName = node.tagName.toLowerCase();
      const nodeText = node.textContent?.trim() || '';

      if (!nodeText && tagName !== 'ul' && tagName !== 'ol') return;

      switch (tagName) {
        case 'h1':
          addSpacing(4);
          addTextBlock(nodeText, 24, 'bold');
          addSpacing(4);
          break;

        case 'h2':
          addSpacing(4);
          addTextBlock(nodeText, 20, 'bold');
          addSpacing(3);
          break;

        case 'h3':
          addSpacing(3);
          addTextBlock(nodeText, 16, 'bold');
          addSpacing(3);
          break;

        case 'h4':
        case 'h5':
        case 'h6':
          addSpacing(2);
          addTextBlock(nodeText, 14, 'bold');
          addSpacing(2);
          break;

        case 'p':
          addSpacing(2);
          addTextBlock(nodeText, 12, 'normal');
          addSpacing(2);
          break;

        case 'ul':
        case 'ol':
          addSpacing(2);
          Array.from(node.children).forEach((li, index) => {
            const bullet = tagName === 'ul' ? '•' : `${index + 1}.`;
            const liText = li.textContent?.trim() || '';
            if (liText) {
              addTextBlock(`${bullet} ${liText}`, 12, 'normal', 10);
              addSpacing(1);
            }
          });
          addSpacing(2);
          break;

        case 'blockquote':
          addSpacing(3);
          addTextBlock(nodeText, 12, 'italic', 20);
          addSpacing(3);
          break;

        case 'pre':
        case 'code':
          addSpacing(2);
          doc.setFont('courier', 'normal');
          addTextBlock(nodeText, 10, 'normal', 10);
          doc.setFont('helvetica', 'normal');
          addSpacing(2);
          break;

        default:
          if (nodeText) {
            addTextBlock(nodeText, 12, 'normal');
          }
      }
    };

    // Process all root-level nodes
    const processRootNodes = (rootElement: HTMLElement) => {
      Array.from(rootElement.children).forEach((child) => {
        processNode(child as HTMLElement);
      });
    };

    processRootNodes(tempDiv.firstChild as HTMLElement);
    doc.save(`${fileName}-transcript.pdf`);
  };

  const downloadRtf = () => {
    const rtfContent = convertToRtf(text);
    const blob = new Blob([rtfContent], { type: 'application/rtf' });
    saveAs(blob, `${fileName}-transcript.rtf`);
    handleClose();
  };

  return (
    <>
      {variant === 'icon' ? (
        <Box
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            cursor: 'pointer',
          }}
        >
          <ListItemIcon
            sx={{
              color: 'text.secondary',
              minWidth: 36,
            }}
          >
            <Download fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Download"
            primaryTypographyProps={{
              variant: 'body2',
            }}
          />
        </Box>
      ) : (
        <Button
          id={id}
          onClick={handleClick}
          startIcon={<Download />}
          variant="outlined"
          size="small"
        >
          Download
        </Button>
      )}
      <ActionDialog
        open={dialogOpen}
        onClose={handleClose}
        title="Download Options"
        actions={[]}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              '& > *': {
                borderRadius: 1,
                py: 0.75,
                px: 1,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          >
            <Box onClick={() => handleDownload(downloadTxt)}>
              <ListItemIcon>
                <FileText fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download as .txt</ListItemText>
            </Box>
            <Box onClick={() => handleDownload(downloadDocx)}>
              <ListItemIcon>
                <FileEdit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download as Word Document</ListItemText>
            </Box>
            <Box onClick={() => handleDownload(downloadPdf)}>
              <ListItemIcon>
                <File fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download as PDF</ListItemText>
            </Box>
            <Box onClick={() => handleDownload(downloadRtf)}>
              <ListItemIcon>
                <FileType fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download as RTF</ListItemText>
            </Box>
          </Box>
        </Box>
      </ActionDialog>
    </>
  );
};

export default DownloadFile;
